<template>
  <div class="h-full flex flex-col">
    <!-- Filter Bar -->
    <div class="px-3 py-2 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <i class="fas fa-filter text-xs text-gray-400"></i>
        <select 
          v-model="filterSort" 
          class="text-xs border-0 bg-transparent text-gray-700 dark:text-gray-300 font-medium focus:ring-0 cursor-pointer hover:text-amber-600 transition-colors"
        >
          <option value="oldest">Oldest First</option>
          <option value="latest">Latest First</option>
          <option value="amount_high">Highest Amount</option>
          <option value="amount_low">Lowest Amount</option>
        </select>
      </div>
    </div>

    <!-- Follow-ups List -->
    <div class="flex-1 overflow-y-auto px-3 space-y-2">
      <div v-for="followup in paginatedFollowUps" :key="followup.id"
        class="group relative p-2.5 rounded-lg bg-gradient-to-br from-white/50 to-white/30 dark:from-dark-700/50 dark:to-dark-700/30 border border-amber-200/50 dark:border-amber-800/30 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-md transition-all duration-200 cursor-pointer"
        @click="openOrder(followup.id)">
        <!-- Urgency Indicator -->
        <div class="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" :class="followup.urgencyClass === 'bg-red-50' ? 'bg-red-500 animate-pulse' : followup.urgencyClass === 'bg-yellow-50' ? 'bg-yellow-500' : 'bg-green-500'"></div>
        
        <div class="space-y-1">
          <div class="flex items-start justify-between gap-2">
            <h4 class="text-xs font-semibold text-gray-900 dark:text-white truncate flex-1">{{ followup.clientName }}</h4>
            <span class="text-xs font-bold text-amber-600 dark:text-amber-400 whitespace-nowrap">{{ formatCurrency(followup.amountDue) }}</span>
          </div>
          
          <div class="flex items-center justify-between text-[10px]">
            <span class="text-blue-600 dark:text-blue-400 font-medium hover:underline">
              Order #{{ followup.orderNumber }}
            </span>
            <span class="px-1.5 py-0.5 rounded-full text-[10px] font-medium" :class="followup.statusColor">
              {{ followup.days_old }} days
            </span>
          </div>
        </div>

        <!-- Hover Effect -->
        <div class="absolute inset-0 rounded-lg bg-gradient-to-br from-amber-400/0 to-orange-400/0 group-hover:from-amber-400/5 group-hover:to-orange-400/5 transition-all pointer-events-none"></div>
      </div>

      <!-- Empty State -->
      <div v-if="followUps.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
        <div class="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center mb-2">
          <i class="fas fa-check-circle text-lg text-amber-600 dark:text-amber-400"></i>
        </div>
        <p class="text-xs font-medium text-gray-900 dark:text-white">All caught up!</p>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">No pending follow-ups</p>
      </div>
    </div>

    <!-- View All Button -->
    <div v-if="(followUps || []).length > perPage" class="p-3 border-t border-gray-200/50 dark:border-dark-700/50">
      <button 
        @click="$emit('view-all')" 
        class="w-full px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-200">
        View All {{ followUps.length }} Follow-ups
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { formatCurrency } from '@/utils/formatters';

const props = defineProps({
  followUps: {
    type: Array,
    default: () => []
  },
  defaultSort: {
    type: String,
    default: 'oldest'
  },
  perPage: {
    type: Number,
    default: 5
  }
});

const emit = defineEmits(['open-order', 'view-all']);

const filterSort = ref(props.defaultSort);
const currentPage = ref(1);

// Reset pagination when filters change
watch(filterSort, () => {
  currentPage.value = 1;
}, { immediate: true });

// Apply filters and sorting
const filteredData = computed(() => {
  let filtered = [...props.followUps];
  
  // Sort the results
  filtered.sort((a, b) => {
    const daysA = parseInt(a.days_old) || 0;
    const daysB = parseInt(b.days_old) || 0;
    
    switch (filterSort.value) {
      case 'latest':
        return daysA - daysB; // Lower days = more recent
      case 'oldest':
        return daysB - daysA; // Higher days = older
      case 'amount_high':
        return parseFloat(b.amountDue) - parseFloat(a.amountDue);
      case 'amount_low':
        return parseFloat(a.amountDue) - parseFloat(b.amountDue);
      default:
        return 0;
    }
  });
  
  return filtered;
});

// Paginate the data
const paginatedFollowUps = computed(() => {
  const startIndex = (currentPage.value - 1) * props.perPage;
  return filteredData.value.slice(startIndex, startIndex + props.perPage);
});

const openOrder = (orderId) => {
  emit('open-order', orderId);
};

const viewAll = () => {
  emit('view-all', { sort: filterSort.value });
};
</script> 